import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { defaultKunde, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    Rechnung,
    addRechnung,
    updateRechnung,
    recalculateRechnung,
    sendRechnung,
    copyRechnung,
    getDefaultRechnung,
    deleteRechnung,
    markRechnungForExport,
    useRechnung,
} from 'api/rechnung';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { RechnungStatus } from 'formComponents/lookups/RechnungStatus';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, createRecordUrl, formatMoney, formatSmDate } from 'utils/formatter';
import { useRechnungPositionList } from 'api/rechnungPosition';
import { AnsprechpartnerSelect } from 'components/Kunde/Ansprechpartner/AnsprechpartnerSelect';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { getApiUrl } from 'api/useApi';
import { SendMail } from 'components/SendMail/SendMail';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { ZusatzadresseSelect } from 'components/Kunde/Zusatzadresse/ZusatzadresseSelect';
import { AraRegelung } from 'formComponents/lookups/AraRegelung';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { ZusatzadresseTyp } from 'api/zusatzadresse';
import { RechnungStatusLabel } from 'utils/labels';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { getInstanceConfig, logDifference } from 'utils/helper';
import { SmRefType } from 'api/attachment';
import { RechnungPositionen } from './RechnungPositionen';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';
import { AddAttachments } from '../History/AddAttachments';

export interface RechnungEditData {
    kNr?: number;
    rNr: string;
}

interface RechnungEditProps {
    defaultKNr?: number;
    rNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (aNr: string) => void;
}

type ButtonAction = 'reopen' | 'print' | 'printOnBriefpapier' | 'send' | 'copy' | 'delete';

const allowedStatus = [0, 1, 5, 6];

export const RechnungEdit: React.FC<RechnungEditProps> = ({ defaultKNr, rNr, onClose, onReopen }) => {
    const instanceConfig = getInstanceConfig();
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [rechnung, setRechnung] = React.useState<Rechnung | null>(getDefaultRechnung(defaultKNr));
    const dbRechnung = useRechnung(
        {
            nr: rNr,
            withAuftrag: true,
            withIsExported: true,
        },
        {
            enabled: rNr !== 'neu',
            onSuccess: (loadedAngebot) => {
                setRechnung(cloneDeep(loadedAngebot));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: rechnung?.kNr,
            withAttachmentList: true,
        },
        {
            enabled: rechnung?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (rNr === 'neu' && rechnung) {
                    setRechnung({
                        ...rechnung,
                        betreuer: newKunde?.betreuer || 0,
                        telBetreuer: newKunde?.telBetreuer || 0,
                        zahlBed: newKunde?.zahlZiel || rechnung.zahlBed,
                    });
                }
            },
        },
    );

    const rechnungPositionList = useRechnungPositionList(
        {
            rNr,
        },
        { enabled: rNr !== 'neu' },
    );

    // const [kunde, setKunde] = React.useState<Kunde | null>(null);
    const [showCopy, setShowCopy] = React.useState(false);
    const [showSend, setShowSend] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    /*
    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kundeList.data[0].nr !== kunde?.nr
        ) {
            setKunde(kundeList.data[0]);
            if (rNr === 'neu' && rechnung) {
                setRechnung({
                    ...rechnung,
                    betreuer: kundeList.data[0]?.betreuer || 0,
                    telBetreuer: kundeList?.data[0]?.telBetreuer || 0,
                    zahlBed: kundeList?.data[0]?.zahlZiel || rechnung.zahlBed,
                });
            }
        }
    }, [kunde, kundeList, rNr, rechnung]);
     */

    /*
    React.useEffect(() => {
        if (!rechnungList.isLoading && !rechnungList.isFetching && rechnung === null) {
            if (rechnungList.data?.length === 1) {
                setRechnung(cloneDeep(rechnungList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                setRechnung(getDefaultRechnung(defaultKNr));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [rechnung, rechnungList, defaultKNr, auth, kundeList]);
     */

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbRechnung.isLoading &&
            !dbRechnung.isFetching &&
            rechnung !== null
        ) {
            if (!isEqual(dbRechnung.data, rechnung)) {
                setSavingState(SavingState.notSaved);
                logDifference('rechnung', rechnung, dbRechnung.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbRechnung, rechnung]);

    const readOnly = React.useMemo(() => {
        return rechnung?.status !== 0;
    }, [rechnung?.status]);

    const validate = React.useCallback((): boolean => {
        if (rechnung?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Kunden aus.');
            return false;
        }
        if (!rechnung?.kaNr) {
            setValidationError('Bitte wählen Sie einen Ansprechpartner aus.');
            return false;
        }

        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [rechnung, validationError]);

    const handleButtonAction = React.useCallback(
        (action?: ButtonAction) => {
            if (!rechnung?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(rechnung.nr);
                    break;
                case 'print':
                    window.open(getApiUrl('rechnung/pdf') + '?nr=' + rechnung.nr);
                    break;
                case 'printOnBriefpapier':
                    window.open(getApiUrl('rechnung/pdf') + '?nr=' + rechnung.nr + '&briefpapier=true');
                    break;
                case 'send':
                    setShowSend(true);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                default:
                    break;
            }
        },
        [rechnung, onReopen],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number) => {
            if (!rechnung) {
                return;
            }

            const updatedRechnung: Rechnung = status === undefined ? rechnung : { ...rechnung, status };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (rNr === 'neu') {
                        addRechnung(updatedRechnung)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateRechnung(updatedRechnung)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbRechnung.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(rechnung.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [dbRechnung, rechnung, rNr, isMounted, onReopen, savingState, validate],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: RechnungStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [
            {
                key: 'print',
                text: 'Drucken mit Briefkopf',
                iconProps: { iconName: 'Print' },
                onClick: () => handleButtonAction('print'),
            },
        ];

        if (rNr !== 'neu' && rechnung?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => handleButtonAction('delete'),
            });
        }

        return { items };
    }, [rNr, rechnung?.status, handleButtonAction]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Rechnung, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(rechnung?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof Rechnung,
        label: string,
        options: FormDecimalFieldOptions = {},
        ignoreReadOnly = false,
    ) => (
        <FormDecimalField
            value={(rechnung?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || (readOnly && !ignoreReadOnly) }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Rechnung, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={rechnung?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    /*
    const renderCheckbox = (column: keyof Rechnung, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={rechnung?.[column] as boolean}
            onChange={(event, checked) => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );
     */

    const renderMa = (column: keyof Rechnung, label: string) => (
        <BetreuerById
            value={rechnung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty
            menuPlacement="top"
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: newValue,
                    });
                }
            }}
        />
    );

    const renderTextbaustein = (
        column: keyof Rechnung,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={rechnung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (column: keyof Rechnung, label: string, required = false, defaultNow = false) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(rechnung?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (rechnung) {
                        setRechnung({
                            ...rechnung,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: readOnly,
                }}
            />
        );
    };

    const renderRechnungStatus = () => (
        <RechnungStatus
            value={rechnung?.status?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            label="Kunde"
            kNr={rechnung?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly || rNr !== 'neu'}
            onChange={(newKNr): void => {
                if (rechnung && rechnung.kNr !== newKNr) {
                    setRechnung({
                        ...rechnung,
                        kNr: newKNr,
                        kaNr: 0,
                        kaName: '',
                        rechnungsAdrNr: 0,
                        rechnungsAdr: undefined,
                    });
                }
            }}
        />
    );

    const renderAnsprechpartner = () => (
        <AnsprechpartnerSelect
            label="Ansprechpartner"
            kNr={rechnung?.kNr}
            kaNr={rechnung?.kaNr}
            kaName={rechnung?.kaName}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newKaNr, newKaName): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        kaNr: newKaNr || 0,
                        kaName: newKaName || '',
                    });
                }
            }}
        />
    );

    const renderZusatzadresse = (
        column: 'rechnungsAdrNr',
        subRecord: 'rechnungsAdr',
        label: string,
        typ: ZusatzadresseTyp,
    ) => (
        <ZusatzadresseSelect
            label={label}
            kNr={rechnung?.kNr}
            zaNr={rechnung?.[column] || 0}
            typ={typ}
            zusatzadresse={rechnung?.[subRecord]}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newZaNr, newZusatzadresse): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        [column]: newZaNr || 0,
                        [subRecord]: newZusatzadresse,
                    });
                }
            }}
        />
    );

    const renderSkontodefinition = () => (
        <Skontodefinition
            value={rechnung?.zahlBed || 0}
            label="Zahlungsbedingungen"
            disabled={savingState === SavingState.saving || readOnly}
            menuPlacement="bottom"
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        zahlBed: newValue,
                    });
                }
            }}
        />
    );

    const renderAraRegelung = () => (
        <AraRegelung
            value={rechnung?.ara || 0}
            label="ARA Regelung"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (rechnung) {
                    setRechnung({
                        ...rechnung,
                        ara: newValue,
                    });
                }
            }}
        />
    );

    const rechnungGutschrift = (rechnung?.sNetto || 0) < 0 ? 'Gutschrift' : 'Rechnung';

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={`${rechnungGutschrift} ${rechnung?.nr || ''} - ${kunde.data?.name1 || '...'} ${
                    kunde.data?.name2 || ''
                } ${kunde.data?.nr ? '(' + kunde.data.nr + ')' : ''}`}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde.data ? <OpenLinkIcon link={createKundeUrl(kunde.data.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell}>{renderAnsprechpartner()}</div>
                            <div className={formStyles.threeColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Rechnung-Nr.', { maxLength: 10, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('auNr', 'Auftrag-Nr.', { maxLength: 10 })}
                                    {rechnung?.auNr ? (
                                        <OpenLinkIcon link={createRecordUrl('auftraege', defaultKNr, rechnung?.auNr)} />
                                    ) : null}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('lNr', 'Lieferung-Nr.', { maxLength: 10 })}
                                    {rechnung?.lNr ? (
                                        <OpenLinkIcon
                                            link={createRecordUrl('lieferungen', defaultKNr, rechnung?.lNr)}
                                        />
                                    ) : null}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Ausstellungsdatum', true, true)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    <FormTextField
                                        value={formatSmDate(rechnung?.bezDat, true)}
                                        savingState={savingState}
                                        options={{ disabled: true }}
                                        label="Bezahlt am"
                                    />
                                </div>
                                {/*
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datBis', 'Auftrag gültig bis')}
                                </div>
                                */}
                            </div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('teilR', 'Teilrechnung #', { disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('trProz', 'Teilrechnung %', { disabled: true })}
                                </div>
                            </div>

                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderMa('betreuer', 'Betreuer')}</div>
                                <div className={formStyles.gridCell}>{renderMa('telBetreuer', 'Tel.-Betreuer')}</div>
                            </div>

                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && rNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.rechnung}
                                        smRef={rNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={formStyles.secondColumn}>
                            <div className={formStyles.gridCell}>{renderTextarea('txt', 'Text')}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('txtB1', 'Textbaustein 1', 25, true)}
                            </div>
                            {instanceConfig.instance === 'easyloop' && (
                                <div className={formStyles.gridCell}>
                                    {renderTextbaustein('txtB2', 'Textbaustein 2', 25, true)}
                                </div>
                            )}
                            <div className={formStyles.gridCell}>
                                {renderZusatzadresse('rechnungsAdrNr', 'rechnungsAdr', 'Rechnungsadresse', 'R')}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderRechnungStatus()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefBed', 'Lieferbedingung', 11, true)}
                            </div>
                            <div className={formStyles.gridCell}>{renderSkontodefinition()}</div>
                            <div className={formStyles.gridCell}>{renderAraRegelung()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('aktText', 'Betreff E-Akt', { maxLength: 100 })}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('fracht', 'Fracht Lager', {}, true)}
                                </div>
                                <div className={formStyles.gridCell}>{renderDecimalField('tipp', 'Tipp-Prov.(%)')}</div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('roadPr', 'Fracht extern', {}, true)}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('ekProzAuf', 'ER-Aufschlag (%)')}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div />
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('mwstCode', 'MwSt.', { fixed: 0, disabled: true })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Kunden)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Kunden)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={rechnung?.auftrag?.zusatz || ''}
                                label="Bemerkung (beim Auftrag)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                    </div>

                    <div>
                        {rechnung && rechnungPositionList?.data && (
                            <RechnungPositionen
                                kunde={kunde.data || defaultKunde}
                                rNr={rechnung.nr}
                                rechnungPositionen={rechnungPositionList?.data}
                                viewMode="long"
                                allowEdit={!readOnly && savingState === SavingState.notChanged}
                                refetch={async () => {
                                    const result = await recalculateRechnung(rechnung);
                                    if (result?.rechnung) {
                                        dbRechnung.refetch().then();
                                        setRechnung({
                                            ...rechnung,
                                            sNetto: result.rechnung.sNetto,
                                            sBrutto: result.rechnung.sBrutto,
                                            sMwst: result.rechnung.sMwst,
                                        });
                                        rechnungPositionList.refetch().then();
                                    }

                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {rechnungPositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(rechnung?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(rechnung?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(rechnung?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={rechnung?.anlageDat}
                        createdDecimalTime={rechnung?.anlageUhr}
                        createdUser={rechnung?.anlageUser}
                        modifiedDate={rechnung?.updDat}
                        modifiedDecimalTime={rechnung?.updUhr}
                        modifiedUser={rechnung?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !rechnung?.nr}
                        />
                        <DefaultButton
                            className={formStyles.sendButton}
                            onClick={() => handleButtonAction('send')}
                            text="Senden"
                            disabled={savingState !== SavingState.notChanged || !rechnung?.nr}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !rechnung?.nr}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !rechnung?.nr}
                            onClick={() => handleButtonAction('printOnBriefpapier')}
                            text="Drucken"
                            menuProps={actionButtonMenuProps}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbRechnung.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbRechnung.isLoading ||
                    dbRechnung.isFetching ||
                    kunde.isLoading ||
                    kunde.isFetching ||
                    !rechnung) && <LoadingIndicator />}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Kunde"
                    disabled={savingState === SavingState.saving || readOnly || rNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={rechnung?.kNr}
                    onChange={async (newKNr) => {
                        if (rechnung && newKNr) {
                            const { newNr } = await copyRechnung(newKNr, rechnung, rechnungPositionList?.data);
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showSend && (
                <SendMail
                    kNr={rechnung?.kNr || 0}
                    kaNr={rechnung?.kaNr}
                    eRechnungDefault
                    markAsSentLabel="Rechnung als versendet markieren"
                    markAsSentDisabled={rechnung?.status === 1 || rechnung?.status === 5 || rechnung?.status === 6}
                    markForExportDisabled={!!rechnung?.isExported}
                    markForExportDefault={!rechnung?.isExported}
                    markForExportLabel="Rechnung für Übergabe an BMD markieren"
                    onSubmit={(kaNrListTo, kaNrListCC, markAsSent, markForExport) => {
                        if (rechnung) {
                            sendRechnung(rechnung, kaNrListTo, kaNrListCC).then(() => {
                                setShowSend(false);
                                if (markAsSent && rechnung) {
                                    save(undefined, 1);
                                }
                                if (markForExport && rechnung) {
                                    markRechnungForExport(rechnung).then();
                                }
                            });
                        }
                    }}
                    onCancel={() => setShowSend(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Rechnung löschen"
                    message={'Soll der Rechnung ' + rechnung?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (rechnung?.nr) {
                            deleteRechnung(rechnung).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => setShowDelete(false)}
                />
            )}
            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    message={
                        'Soll der Status von Rechnung ' +
                        rechnung?.nr +
                        ' auf "' +
                        RechnungStatusLabel[changeStatusValue] +
                        '" geändert werden?'
                    }
                    onConfirm={() => {
                        if (rechnung?.nr) {
                            save(() => setChangeStatusValue(null), changeStatusValue);
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && rNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.rechnung}
                    forcedSmRef={rNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
